import { useEffect, useRef } from 'react';

import { AudioLevelAnalyser } from '@yandex-360/telemost-sdk/audio-diagnostics';
import debounce from 'lodash/debounce';

import { useActiveDeviceStream } from '../features/device-manager/hooks';
import { audioLevelAnalyser } from '../features/goloom-sdk';
import { getAudioContext } from '../helpers/audio-context';

const audioContext = getAudioContext();
type UseMicrophoneLevelProps =
    | {
          multiFactor?: number;
          externalStream?: MediaStream;
          customAudioLevelAnalyser?: AudioLevelAnalyser;
      }
    | undefined;
export function useMicrophoneLevel(props: UseMicrophoneLevelProps) {
    const { multiFactor = 1, externalStream, customAudioLevelAnalyser } = props ?? {};

    const microphoneLevel = useRef<number>(0);
    const stream = useActiveDeviceStream('audioinput', true);
    const currentAudioLevelAnalyser = customAudioLevelAnalyser ?? audioLevelAnalyser;

    // Эффект, реагирующий на стрим, получающий значение громкости
    useEffect(() => {
        const currentStream = externalStream ?? stream;

        if (!currentStream || !audioContext || !currentAudioLevelAnalyser) {
            return;
        }

        if (audioContext.state !== 'running') {
            audioContext.resume();
        }

        const sourceNode = audioContext.createMediaStreamSource(currentStream);
        currentAudioLevelAnalyser.connect(sourceNode);

        /**
         * Из analyzer почему-то приходят низкие значения громкости.
         * Временно (?) вставляем костыль здесь чтобы получать данные, подходящие для отображения в UI
         */
        const setAdjustedValue = (audioLvl: number) => {
            const adjustedValue = Math.min(audioLvl * multiFactor, 1);
            microphoneLevel.current = adjustedValue;
        };

        const resetLevelAfterTime = debounce(() => setAdjustedValue(0), 200);

        const analyzerCallback = (audioLvl: number) => {
            resetLevelAfterTime();
            setAdjustedValue(audioLvl);
        };

        currentAudioLevelAnalyser.start({
            callback: analyzerCallback,
        });

        return () => {
            currentAudioLevelAnalyser?.disconnect(sourceNode);
            currentAudioLevelAnalyser?.stop(analyzerCallback);
        };
    }, [externalStream, stream, multiFactor, currentAudioLevelAnalyser]);

    return microphoneLevel;
}
